import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, UserCheck, Clock, CheckCircle, Trash2, Loader } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const ProfileBlockerModal = ({ isOpen, onClose, userStatus, userName, userId }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen) return null;

  const isNew = userStatus === 'NEW';
  const isPending = userStatus === 'PENDING_APPROVAL';

  const handleCompleteProfile = () => {
    navigate('/profile', { state: { editMode: true } });
    onClose();
  };

  const handleDeleteAccount = async () => {
    if (!userId) {
      toast.error('Unable to delete account. Please try again.');
      return;
    }

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('shootzu_token');
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/account/${userId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Clear all local storage and redirect
      localStorage.clear();
      toast.success('Account deleted successfully.');
      navigate('/login');
    } catch (error) {
      console.error('Delete account error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20 }}
    >
      <div
        className="relative w-full max-w-md"
        style={{ background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', fontFamily: "'Work Sans', sans-serif" }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 flex items-center justify-center transition-colors"
          style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', color: '#fff' }}
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div
          className="px-6 py-6 relative"
          style={{
            background: isNew
              ? 'linear-gradient(120deg,#572B7C,#78287C)'
              : 'linear-gradient(120deg,#B45309,#78350F)',
            borderTopLeftRadius: 22,
            borderTopRightRadius: 22,
            overflow: 'hidden',
          }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: -24, top: -24, width: 130, height: 130, opacity: 0.06, animation: 'szspin 80s linear infinite', pointerEvents: 'none' }}
          />
          <div className="flex items-center gap-3 text-white relative">
            <div className="flex items-center justify-center" style={{ padding: 12, borderRadius: '9999px', background: 'rgba(255,255,255,.18)' }}>
              {isNew ? <UserCheck size={32} /> : <Clock size={32} />}
            </div>
            <div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {isNew ? 'Profile Required' : 'Profile Under Review'}
              </h2>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6" style={{ color: '#fff' }}>
          {isNew ? (
            // NEW user content
            <div className="space-y-4">
              <p className="text-lg" style={{ color: 'rgba(255,255,255,.8)' }}>
                You need to complete your profile before accessing this feature.
              </p>

              <div className="p-4" style={{ borderRadius: 12, background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.28)' }}>
                <div className="flex gap-3">
                  <UserCheck className="flex-shrink-0 mt-0.5" style={{ color: '#28C0D0' }} size={20} />
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: '#fff' }}>
                      Complete your profile to:
                    </h3>
                    <ul className="text-sm space-y-1" style={{ color: 'rgba(255,255,255,.6)' }}>
                      <li className="flex items-center gap-2">
                        <span style={{ width: 6, height: 6, borderRadius: '9999px', background: '#28C0D0' }} />
                        Post jobs
                      </li>
                      <li className="flex items-center gap-2">
                        <span style={{ width: 6, height: 6, borderRadius: '9999px', background: '#28C0D0' }} />
                        Apply for work
                      </li>
                      <li className="flex items-center gap-2">
                        <span style={{ width: 6, height: 6, borderRadius: '9999px', background: '#28C0D0' }} />
                        Send messages
                      </li>
                      <li className="flex items-center gap-2">
                        <span style={{ width: 6, height: 6, borderRadius: '9999px', background: '#28C0D0' }} />
                        Request quotes
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleCompleteProfile}
                  className="flex-1 font-semibold py-3 hover:opacity-90"
                  style={{ background: '#28C0D0', color: '#06121A' }}
                >
                  Complete Profile
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  className="flex-1"
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,.14)', color: '#fff' }}
                >
                  Close
                </Button>
              </div>

              {/* Delete Account */}
              {userId && (
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,.12)' }}>
                  {!showDeleteConfirm ? (
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full text-xs transition-colors hover:text-red-400"
                      style={{ color: 'rgba(255,255,255,.4)' }}
                    >
                      Delete my account
                    </button>
                  ) : (
                    <div className="p-3" style={{ borderRadius: 12, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(185,28,28,.4)' }}>
                      <p className="text-xs mb-3" style={{ color: '#F87171' }}>
                        Are you sure? This will permanently delete your account and all associated data. This action cannot be undone.
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleDeleteAccount}
                          disabled={isDeleting}
                          className="flex-1 flex items-center justify-center gap-1 text-xs font-medium text-white disabled:opacity-50 py-2 transition-colors"
                          style={{ background: '#B91C1C', borderRadius: 10 }}
                        >
                          {isDeleting ? (
                            <><Loader size={12} className="animate-spin" /> Deleting...</>
                          ) : (
                            <><Trash2 size={12} /> Yes, delete my account</>
                          )}
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isDeleting}
                          className="flex-1 text-xs font-medium py-2 transition-colors"
                          style={{ color: '#fff', background: 'transparent', border: '1px solid rgba(255,255,255,.14)', borderRadius: 10 }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : isPending ? (
            // PENDING_APPROVAL user content
            <div className="space-y-4">
              <div className="text-center mb-4">
                <div className="mx-auto flex items-center justify-center mb-4" style={{ width: 64, height: 64, borderRadius: '9999px', background: 'rgba(180,83,9,.18)', border: '1px solid rgba(180,83,9,.4)' }}>
                  <Clock style={{ color: '#FCD34D' }} size={32} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#fff', fontFamily: "'Poppins', sans-serif" }}>
                  Your profile is under review
                </h3>
              </div>

              <div className="p-4" style={{ borderRadius: 12, background: 'rgba(180,83,9,.18)', border: '1px solid rgba(180,83,9,.4)' }}>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,.7)' }}>
                  Thank you for submitting your profile! Our admin team is currently reviewing your information.
                  This usually takes less than 24 hours.
                </p>
              </div>

              <div className="p-4" style={{ borderRadius: 12, background: 'rgba(103,198,180,.1)', border: '1px solid rgba(103,198,180,.34)' }}>
                <div className="flex gap-3">
                  <CheckCircle className="flex-shrink-0 mt-0.5" style={{ color: '#67C6B4' }} size={20} />
                  <div>
                    <h4 className="font-semibold mb-1" style={{ color: '#67C6B4' }}>
                      We'll notify you by email
                    </h4>
                    <p className="text-sm" style={{ color: 'rgba(255,255,255,.6)' }}>
                      As soon as your profile is approved, you'll receive an email and have full access to all Shootzu features.
                    </p>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <div className="mt-6">
                <Button
                  onClick={onClose}
                  className="w-full font-semibold py-3 hover:opacity-90"
                  style={{ background: '#28C0D0', color: '#06121A' }}
                >
                  Got it
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ProfileBlockerModal;
